import { useEffect, useMemo, useState } from 'react';
import { Loader } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { Button, CachedImage, Modal, MultiSelect } from '@shared/ui';
import {
  ExtraRuleTypeFrom,
  ExtraRuleTypeTo,
  extrasApi,
  useExtras,
  type ExtraRule,
} from '@modules/extras';
import { useProducts, productThumbSrc, type ProductCardResponse } from '@modules/products';
import './ManageProductExtrasModal.css';

const MAX_EXTRAS = 5;

function isProductDedicatedRule(rule: ExtraRule, productId: number): boolean {
  if (rule.typeTo !== ExtraRuleTypeTo.PRODUCTS) return false;
  if (rule.typeFrom !== ExtraRuleTypeFrom.PRODUCTS) return false;
  const idsTo = Array.isArray(rule.idsTo) ? rule.idsTo.map(Number) : [];
  return idsTo.length === 1 && idsTo[0] === productId;
}

interface ManageProductExtrasModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number;
  onSuccess?: () => void;
}

export default function ManageProductExtrasModal({
  isOpen,
  onClose,
  productId,
  onSuccess,
}: ManageProductExtrasModalProps) {
  const queryClient = useQueryClient();
  const { data: rules = [], isLoading: rulesLoading, refetch } = useExtras();
  const { data: products = [], isLoading: productsLoading } = useProducts();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const dedicatedRules = useMemo(
    () => rules.filter((rule) => isProductDedicatedRule(rule, productId)),
    [rules, productId],
  );

  useEffect(() => {
    if (!isOpen) return;
    const fromRules = dedicatedRules.flatMap((rule) =>
      (Array.isArray(rule.idsFrom) ? rule.idsFrom : []).map(Number),
    );
    const unique = [...new Set(fromRules)]
      .filter((id) => id !== productId)
      .slice(0, MAX_EXTRAS);
    setSelectedIds(unique);
  }, [isOpen, dedicatedRules, productId]);

  const productById = useMemo(() => {
    const map = new Map<number, ProductCardResponse>();
    products.forEach((p) => map.set(p.id, p));
    return map;
  }, [products]);

  const options = useMemo(
    () =>
      products
        .filter((p) => p.id !== productId)
        .map((p) => ({
          value: String(p.id),
          label: `ID ${p.id}: ${p.name}`,
        })),
    [products, productId],
  );

  const selectedProducts = useMemo(
    () =>
      selectedIds
        .map((id) => productById.get(id))
        .filter((p): p is ProductCardResponse => Boolean(p)),
    [selectedIds, productById],
  );

  const handleChange = (values: (string | number)[]) => {
    const next = values
      .map((v) => Number(v))
      .filter((id) => !Number.isNaN(id) && id !== productId);
    setSelectedIds(next.slice(0, MAX_EXTRAS));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const rule of dedicatedRules) {
        await extrasApi.delete(rule.id);
      }
      if (selectedIds.length > 0) {
        await extrasApi.create({
          typeTo: ExtraRuleTypeTo.PRODUCTS,
          idsTo: [productId],
          typeFrom: ExtraRuleTypeFrom.PRODUCTS,
          idsFrom: selectedIds.slice(0, MAX_EXTRAS),
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['extras'] });
      await refetch();
      notifications.show({
        title: 'Успешно',
        message: 'Дополнительные предложения сохранены',
        color: 'green',
      });
      onSuccess?.();
      onClose();
    } catch (error: any) {
      notifications.show({
        title: 'Ошибка',
        message:
          error?.response?.data?.message ||
          'Не удалось сохранить дополнительные предложения',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const loading = rulesLoading || productsLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Дополнительные предложения"
      maxWidth={560}
    >
      <div className="manage-product-extras">
        <p className="manage-product-extras__hint">
          Выберите до {MAX_EXTRAS} товаров, которые предложат к этому букету
          (открытки, вазы и т.п.).
        </p>

        {loading ? (
          <div className="manage-product-extras__loader">
            <Loader />
          </div>
        ) : (
          <>
            <MultiSelect
              label="Товары-допы"
              placeholder={
                selectedIds.length >= MAX_EXTRAS
                  ? `Максимум ${MAX_EXTRAS}`
                  : 'Выберите товары…'
              }
              data={options}
              value={selectedIds}
              onChange={handleChange}
              maxValues={MAX_EXTRAS}
              searchable
              disabled={options.length === 0}
            />

            {selectedProducts.length > 0 ? (
              <div className="manage-product-extras__preview">
                {selectedProducts.map((item) => {
                  const image = item.images?.[0];
                  return (
                    <div
                      key={item.id}
                      className="manage-product-extras__thumb"
                      title={item.name}
                    >
                      {image ? (
                        <CachedImage
                          src={productThumbSrc(image) || image.url}
                          alt={item.name}
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="manage-product-extras__thumb-empty"
                          aria-hidden
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="manage-product-extras__empty">Пока ничего не выбрано.</p>
            )}

            <div className="manage-product-extras__actions">
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Отмена
              </Button>
              <Button onClick={handleSave} loading={saving} disabled={saving}>
                Сохранить
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
